import React, { useMemo } from 'react';
import type { LocationObjectCoords } from 'expo-location';
import type { AddressType, PinData, Region } from '@sweetgreen/garnish';
import {
  getCustomerGeolocationPin,
  useEffectSkipped,
} from '@sweetgreen/garnish';

import { useCustomer } from '@order/Customer';

import type { LocationResultData } from '../GraphQL';
import { minimizePins, selectActivePin } from '../LocationsScreen.utils';

// Controls display of search area button (non-delivery only).
export const useSearchAreaBtn = ({
  region,
  hasNoResults,
  isDelivery,
}: {
  readonly region: Region;
  readonly hasNoResults?: boolean;
  readonly isDelivery?: boolean;
}) => {
  const [isShown, setIsShown] = React.useState(true);
  const [isRegionChangeInProgress, setIsRegionChangeInProgress] =
    React.useState(false);
  const toggleBtnBasedOnRegion = React.useCallback(() => {
    const shouldShowBtn = !isRegionChangeInProgress;

    setIsShown(shouldShowBtn);
    setIsRegionChangeInProgress(false);
  }, [isRegionChangeInProgress]);
  const showBtn = React.useCallback(() => {
    setIsShown(true);
  }, []);
  const hideBtn = React.useCallback(() => {
    setIsShown(false);
  }, []);

  useEffectSkipped(() => {
    setIsRegionChangeInProgress(true);
    setIsShown(false);
  }, [region]);

  // Always show the search button when there are no results.
  const showSearchButtonWithNoResults = Boolean(hasNoResults);

  // Never show the search button when in delivery.
  const hideSearchButtonWhenDelivery = Boolean(isDelivery);

  return {
    isShown: hideSearchButtonWhenDelivery
      ? false
      : showSearchButtonWithNoResults || isShown,
    toggleBtnBasedOnRegion,
    showBtn,
    hideBtn,
  };
};

// Returns customer geolocation, delivery or pickup/outpost pins to show on map.
export const usePins = (props: UsePinsProps) => {
  const {
    deliveryPin,
    locationSearchType,
    selectedLocation,
    locations,
    userGeolocation,
  } = props;

  // ─────────────────────────────────────────────────────────────────

  const { customer } = useCustomer();
  const customerName = customer?.firstName ?? '';
  const customerGeoPositionPinData = getCustomerGeolocationPin({
    geolocation: userGeolocation,
    customerName,
  });

  // ─────────────────────────────────────────────────────────────────

  return useMemo(() => {
    const isDeliveryLocationSearch = locationSearchType === 'delivery';

    const customerGeoPositionPin = customerGeoPositionPinData
      ? [customerGeoPositionPinData]
      : [];
    const deliveryLocationPin = deliveryPin ? [deliveryPin] : [];
    const locationsPins = minimizePins(
      locationSearchType,
      selectActivePin(selectedLocation, locations),
    );

    return [
      ...customerGeoPositionPin,
      ...(isDeliveryLocationSearch ? deliveryLocationPin : locationsPins),
    ];
  }, [
    customerGeoPositionPinData,
    deliveryPin,
    locationSearchType,
    locations,
    selectedLocation,
  ]);
};

// ─── Types ──────────────────────────────────────────────────────────────────────

type UsePinsProps = {
  readonly deliveryPin: PinData | null;
  readonly locationSearchType: AddressType;
  readonly selectedLocation: string;
  readonly locations: ReadonlyArray<PinData & LocationResultData>;
  readonly userGeolocation?: LocationObjectCoords;
};
